import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { translatableZodSchema } from "@@/shared/translations/translation";
import { customerIdZodSchema } from "@@/shared/users/customer-id";
import { userIdZodSchema } from "@@/shared/users/user-id";
import { z } from "zod";
import { managePaymentCommandIdZodSchema } from "../../manage-payments/manage-payment-command-id";
import type { PayRequestId} from "../pay-request-id";
import { payRequestIdZodSchema } from "../pay-request-id";
import type { PayRequestEvent } from "./_pay-request.event";
import { payRequestEventIdFactory } from "./_pay-request.event-id";
import { payRequestEventSchemaFactory } from "./_pay-request.event.schema-factory";

const payRequestCancelledEventZodObject = payRequestEventSchemaFactory(
    "PAY_REQUEST_CANCELLED_EVENT",
    z.object({
        cancelledBy: userIdZodSchema.or(customerIdZodSchema),
        cancelledAt: isoAndUnixZodSchema,
        reason: translatableZodSchema,
        type: z.literal("PAY_REQUEST_CANCELLED_EVENT"),
        /**
         * If another pay request replaces this one, the new id of the
         * replacing pay request is stored here
         */
        replacementPayRequestId: payRequestIdZodSchema.optional(),
        /**
         * Contains id's/tags for grouping the event with others.
         * Luke a payment change command id that can be used to find and
         * group all events related to a single payment change command
         */
        references: z.object({
            managePaymentCommandId: managePaymentCommandIdZodSchema.optional(),
        }),
    }),
);
type PayRequestCancelledEvent = z.infer<
    typeof payRequestCancelledEventZodObject
>;
const payRequestCancelledEventZodSchema: z.ZodSchema<PayRequestCancelledEvent> =
    payRequestCancelledEventZodObject;

type PayRequestCancelledEventFactoryParams = {
    payRequestId: PayRequestId;
    data: PayRequestCancelledEvent["data"];
    paymentFor: PayRequestEvent["paymentFor"];
    version: number;
};
const payRequestCancelledEventFactory = (
    params: PayRequestCancelledEventFactoryParams,
) => {
    const now = nowIsoAndUnix();
    const event: PayRequestCancelledEvent = {
        _id: payRequestEventIdFactory(),
        _type: "PAY_REQUEST_EVENT",
        _createdAt: now,
        _happenedAt: now,
        _version: params.version,
        type: "PAY_REQUEST_CANCELLED_EVENT",
        data: params.data,
        payRequestId: params.payRequestId,
        paymentFor: params.paymentFor,
    };
    return payRequestCancelledEventZodSchema.parse(event);
};

export {
    payRequestCancelledEventFactory,
    payRequestCancelledEventZodObject,
    payRequestCancelledEventZodSchema,
};
export type { PayRequestCancelledEvent };
